import { useRef, useState, type ChangeEvent } from 'react';
import { bytesToBlob } from '../../crypto/crypto';
import { prepareImageUpload } from '../../crypto/uploadImage';
import { extractVideoPosterFrame } from '../../crypto/videoPoster';
import { useEncryption } from '../../crypto/EncryptionProvider';
import { useDeleteMedia, useMilestoneMedia, useUploadMedia } from '../../data/queries';
import { useBabyCaps } from '../../data/permissions';
import { useFileDrop } from '../../hooks/useFileDrop';
import type { MediaRow } from '../../data/dto';
import { MediaLightbox } from './MediaLightbox';
import { MediaEngageTile } from '../engagement/MediaEngageTile';

interface Props {
  babyId: string;
  milestoneId: number;
  onOpenMedia?: () => void;
}

/**
 * Per-milestone photo gallery with real upload/list/decrypt/delete.
 *
 * Upload pipeline: pick → compress (≤3000px sum) → optionally encrypt (E2EE) →
 * multipart POST. Display pipeline lives in <MediaTile>: fetch raw bytes →
 * decrypt if needed → object URL.
 */
export function MilestoneMemories({ babyId, milestoneId, onOpenMedia }: Props) {
  const enc = useEncryption();
  const caps = useBabyCaps(babyId);
  const mediaQ = useMilestoneMedia(babyId, milestoneId);
  const upload = useUploadMedia();
  const del = useDeleteMedia();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const items = mediaQ.data ?? [];
  const hasEncrypted = items.some((m) => m.is_e2e_encrypted);
  const canDecrypt = enc.status === 'ready';
  const locked = enc.status === 'locked';

  const pick = async () => {
    setError(null);
    // Make sure the user has chosen an encryption mode (and unlocked) first.
    const ready = await enc.ensureReady();
    if (!ready) return;
    inputRef.current?.click();
  };

  const processFiles = async (files: File[]) => {
    if (files.length === 0) return;

    setError(null);
    const ready = await enc.ensureReady();
    if (!ready) return;

    setUploading(true);
    setProgress({ done: 0, total: files.length });
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        const isVideo = file.type.startsWith('video/') || /\.(mp4|mov|m4v|webm|ogv|avi|mkv|3gp)$/i.test(file.name);
        const form = new FormData();

        if (isVideo) {
          if (file.size > 50 * 1024 * 1024) {
            throw new Error(`Video "${file.name}" exceeds the 50MB limit.`);
          }
          // Video: encrypt the raw bytes, plus a client-grabbed poster-frame
          // thumbnail — the server has no video decoder to make one itself.
          const buffer = await file.arrayBuffer();
          const { bytes, encrypted } = enc.encryptForUpload(new Uint8Array(buffer));
          form.append('file', bytesToBlob(bytes, 'application/octet-stream'), `${file.name}.bin`);
          const poster = await extractVideoPosterFrame(file);
          if (poster) {
            const posterBytes = new Uint8Array(await poster.arrayBuffer());
            const thumbBytes = encrypted ? enc.encryptForUpload(posterBytes).bytes : posterBytes;
            form.append('thumbnail', bytesToBlob(thumbBytes, 'application/octet-stream'), `${file.name}.thumb.bin`);
          }
          form.append('is_encrypted', encrypted ? '1' : '0');
          form.append('mime_type', file.type || 'video/mp4');
        } else {
          if (file.size > 20 * 1024 * 1024) {
            throw new Error(`Photo "${file.name}" exceeds the 20MB limit.`);
          }
          // Image: compress + encrypt full, plus an encrypted thumbnail for E2EE.
          const parts = await prepareImageUpload(file, enc.encryptForUpload);
          form.append('file', parts.fileBlob, `${file.name}.bin`);
          if (parts.thumbBlob) form.append('thumbnail', parts.thumbBlob, `${file.name}.thumb.bin`);
          form.append('is_encrypted', parts.encrypted ? '1' : '0');
          form.append('mime_type', parts.mime);
          if (parts.width > 0) form.append('width_px', String(parts.width));
          if (parts.height > 0) form.append('height_px', String(parts.height));
        }
        await upload.mutateAsync({ babyId, milestoneId, form });
        setProgress({ done: i + 1, total: files.length });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const onFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    void processFiles(files);
  };

  const { isDraggingOver, dropProps } = useFileDrop({
    onDropFiles: processFiles,
    disabled: !caps.canUploadMedia || uploading,
  });

  return (
    <div>
      {/* 3-up grid; tiles are square and fill their column for a bigger preview. */}
      <div
        {...dropProps}
        className={[
          'grid grid-cols-3 gap-2 rounded-xl p-1.5 -m-1.5 transition-all duration-150',
          isDraggingOver ? 'bg-accent/10 ring-2 ring-accent ring-dashed' : '',
        ].join(' ')}
      >
        {items.map((m: MediaRow, i: number) => (
          <MediaEngageTile
            key={m.id}
            media={m}
            babyId={babyId}
            canDecrypt={canDecrypt}
            decrypt={enc.decrypt}
            onOpen={() => setLightboxIndex(i)}
            onUnlock={() => void enc.ensureReady()}
            onDelete={caps.canWrite ? () => del.mutate({ mediaId: m.id, babyId, milestoneId }) : undefined}
          />
        ))}

        {caps.canUploadMedia && (
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            className={[
              'flex aspect-square w-full flex-col items-center justify-center gap-1.5 rounded-xl text-accent-ink disabled:opacity-60 cursor-pointer transition-all',
              isDraggingOver ? 'scale-105' : '',
            ].join(' ')}
            style={{
              border: isDraggingOver
                ? '2px dashed var(--color-accent)'
                : '1.5px dashed color-mix(in oklch, var(--color-accent) 35%, var(--color-line))',
              background: isDraggingOver
                ? 'color-mix(in oklch, var(--color-accent) 15%, var(--color-bg))'
                : 'color-mix(in oklch, var(--color-accent) 5%, var(--color-bg))',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="6" width="18" height="14" rx="2.5" />
              <circle cx="12" cy="13" r="3.5" />
              <path d="M8 6l1.5-2h5L16 6" />
            </svg>
            <span className="px-0.5 text-[10px] font-semibold uppercase tracking-wider leading-tight text-center">
              {isDraggingOver ? 'Drop photo' : items.length === 0 ? 'Add photo' : 'Add'}
            </span>
          </button>
        )}
      </div>

      {/* Status line */}
      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] tracking-wide text-ink-2">
        {enc.isEncryptedMode ? (
          <span className="inline-flex items-center gap-1 font-semibold text-accent-ink">
            <LockGlyph /> End-to-end encrypted
          </span>
        ) : enc.mode === 'none' ? (
          <span className="text-ink-3">Stored without encryption</span>
        ) : null}
        {items.length > 0 && <span className="text-ink-3">· {items.length} {items.length === 1 ? 'photo' : 'photos'}</span>}
      </div>

      {uploading && progress && (
        <p className="mt-2 text-[12px] text-ink-2">
          Uploading {progress.done + 1 > progress.total ? progress.total : progress.done + 1} of {progress.total}
          {enc.isEncryptedMode ? ' · encrypting on your device…' : '…'}
        </p>
      )}

      {locked && hasEncrypted && (
        <button onClick={() => void enc.ensureReady()} className="mt-2 text-[12.5px] font-semibold text-accent-ink underline">
          Enter your password to view these photos
        </button>
      )}

      {error && <p className="mt-2 text-[12px] leading-snug text-[#7E3320]">{error}</p>}

      <input ref={inputRef} type="file" accept="image/*,video/*" multiple onChange={onFiles} className="hidden" />

      {lightboxIndex !== null && items[lightboxIndex] && (
        <MediaLightbox
          items={items}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          canDecrypt={canDecrypt}
          decrypt={enc.decrypt}
          babyId={babyId}
          onOpenMedia={onOpenMedia}
          onDelete={caps.canWrite ? (m) => del.mutateAsync({ mediaId: m.id, babyId, milestoneId }) : undefined}
        />
      )}
    </div>
  );
}

function LockGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
